import type { User } from '@supabase/supabase-js';
import { toast } from 'sonner';
import { typedRpcValue } from '@/lib/supabaseTypedQuery';
import { log } from '@/lib/logger';

export type SsoClaimStatus =
  | 'joined'
  | 'already_member'
  | 'not_sso'
  | 'no_connection'
  | 'seat_limit_reached'
  | 'domain_not_allowed';
export interface SsoClaimResult {
  status: SsoClaimStatus;
  workspace_id?: string;
}

export const SSO_SEAT_LIMIT_MESSAGE = 'Your workspace has no free seats. Ask your admin to add one.';
export const SSO_DOMAIN_NOT_ALLOWED_MESSAGE =
  'Your email address is not on one of your company\'s verified domains, so company sign-in cannot add you to the workspace. Ask your admin to send you an invitation.';

export function emailDomain(email: string): string | null {
  const at = email.trim().toLowerCase().lastIndexOf('@');
  if (at < 1) return null;
  const domain = email.trim().toLowerCase().slice(at + 1);
  return /^[a-z0-9.-]+\.[a-z]{2,}$/.test(domain) ? domain : null;
}

/**
 * SSO accounts are separate auth users created by the SAML provider, so the
 * account itself says whether to claim; no session read is needed.
 */
export function isSsoUser(user: Pick<User, 'app_metadata'> | null): boolean {
  const meta = user?.app_metadata as { provider?: unknown; providers?: unknown } | undefined;
  const provider = typeof meta?.provider === 'string' ? meta.provider : '';
  const providers = Array.isArray(meta?.providers) ? meta.providers : [];
  return provider.startsWith('sso') || providers.some(p => typeof p === 'string' && p.startsWith('sso'));
}

/**
 * After an SSO sign-in, join the workspace that owns the SSO connection.
 * Safe to call repeatedly. Returns null for non-SSO sessions or on failure.
 */
export async function claimSsoMembership(user: Pick<User, 'app_metadata'> | null): Promise<SsoClaimResult | null> {
  if (!isSsoUser(user)) return null;
  let data: SsoClaimResult | null = null;
  let error: { message?: string } | null = null;
  try {
    ({ data, error } = await typedRpcValue<SsoClaimResult>('app_sso_claim_membership'));
  } catch (caught) {
    error = { message: caught instanceof Error ? caught.message : String(caught) };
  }
  if (error || !data) {
    log.error('SSO membership claim failed', { error: error?.message });
    return null;
  }
  if (data.status === 'seat_limit_reached') toast.error(SSO_SEAT_LIMIT_MESSAGE);
  if (data.status === 'domain_not_allowed') toast.error(SSO_DOMAIN_NOT_ALLOWED_MESSAGE);
  return data;
}
